/**
 * @fileoverview V2 Stepper — Vertical sidebar navigation.
 *
 * Design: Screening V2 floating-pill aesthetic, adapted for light mode.
 * - Rounded container with soft shadow
 * - Icon buttons with tooltip labels
 * - Active = filled blue, completed = green check, locked = muted
 */
import { Check, Lock } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';

import { type StepCompletionStatus, useStepCompletion } from '../hooks/useStepCompletion';
import { useIngestion } from '../state/IngestionProvider';
import { CREATION_STEP_COUNT, type StepDef, STEPS } from './steps';

interface TooltipProps {
  label: string;
  sublabel?: string;
  children: React.ReactNode;
}

interface StepButtonProps {
  step: StepDef;
  active: boolean;
  completed: boolean;
  locked: boolean;
  onClick: () => void;
}

interface StepStatus {
  active: boolean;
  completed: boolean;
  locked: boolean;
}

const Tooltip = ({ label, sublabel, children }: TooltipProps): React.JSX.Element => {
  const [show, setShow] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div
          className={cn(
            'absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2',
            'whitespace-nowrap rounded-lg bg-zinc-800 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg',
            'animate-in fade-in slide-in-from-left-1 duration-150',
            'before:absolute before:right-full before:top-1/2 before:-translate-y-1/2',
            'before:border-4 before:border-transparent before:border-r-zinc-800',
          )}
        >
          {label}
          {sublabel && (
            <span className="mt-0.5 block text-[10px] text-zinc-400">{sublabel}</span>
          )}
        </div>
      )}
    </div>
  );
}

/** Map step IDs to completion status keys */
const STEP_TO_COMPLETION: Record<string, string> = {
  basics: 'basics',
  case_info: 'case_info',
  client_info: 'client_info',
  companions: 'companions',
  liability: 'liability',
  treatment: 'treatment',
  coverage: 'coverage',
  documents: 'documents',
  notes: 'notes',
};

function getStepStatus(
  step: StepDef,
  index: number,
  activeIndex: number,
  matterId: string | null,
  completion: StepCompletionStatus,
): StepStatus {
  const locked = step.phase === 'ingestion' && !matterId;
  const active = index === activeIndex;
  const completionKey = STEP_TO_COMPLETION[step.id] as keyof StepCompletionStatus | undefined;
  const completed = completionKey ? !!completion[completionKey] : false;

  return { active, completed, locked };
}

const StepIcon = ({
  icon: Icon,
  active,
  completed,
  locked,
}: {
  icon: StepDef['icon'];
  active: boolean;
  completed: boolean;
  locked: boolean;
}): React.JSX.Element => {
  if (locked) {
    return <Lock className="h-3.5 w-3.5" />;
  }

  if (completed && !active) {
    return <Check className="h-3.5 w-3.5" />;
  }

  return <Icon className="h-3.5 w-3.5" />;
}

const StepButton = ({
  step,
  active,
  completed,
  locked,
  onClick,
}: StepButtonProps): React.JSX.Element => {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={locked}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-full px-2.5 py-1.5',
        'transition-all duration-150',
        active && 'bg-blue-600 text-white shadow-sm',
        !active && completed && 'text-emerald-600 hover:bg-emerald-50',
        !active && !completed && locked && 'cursor-not-allowed text-zinc-300',
        !active && !completed && !locked && 'text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700',
      )}
    >
      <span className="flex h-6 w-6 shrink-0 items-center justify-center">
        <StepIcon
          icon={step.icon}
          active={active}
          completed={completed}
          locked={locked}
        />
      </span>
      <span
        className={cn(
          'truncate text-xs font-medium',
          active && 'text-white',
          !active && completed && 'text-emerald-600',
          !active && !completed && locked && 'text-zinc-300',
          !active && !completed && !locked && 'text-zinc-500',
        )}
      >
        {step.label}
      </span>
    </button>
  );

  if (!locked) {
    return button;
  }

  return (
    <Tooltip label={step.label} sublabel="Create matter first">
      {button}
    </Tooltip>
  );
}

export const Stepper = (): React.JSX.Element => {
  const { state, requestNavigate } = useIngestion();
  const completionStatus = useStepCompletion(state.matterId);

  return (
    <div className="flex w-40 flex-col items-start gap-2">
      <div className="flex w-full flex-col gap-0.5 rounded-2xl border border-zinc-100 bg-white p-1.5 shadow-md">
        {STEPS.map((step, index) => {
          const { active, completed, locked } = getStepStatus(
            step,
            index,
            state.stepIndex,
            state.matterId,
            completionStatus,
          );

          function handleClick(): void {
            if (!locked) {
              requestNavigate(index);
            }
          }

          return (
            <StepButton
              key={step.id}
              step={step}
              active={active}
              completed={completed}
              locked={locked}
              onClick={() => handleClick()}
            />
          );
        })}
      </div>
    </div>
  );
}
